import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

const OrdersContext = createContext();
const ORDERS_CACHE_PREFIX = 'userOrdersCache:';

const readCachedOrders = (userId) => {
  if (!userId || typeof window === 'undefined') return null;

  try {
    const stored = window.sessionStorage.getItem(`${ORDERS_CACHE_PREFIX}${userId}`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error reading orders cache:', error);
    return null;
  }
};

const writeCachedOrders = (userId, orders) => {
  if (!userId || typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(`${ORDERS_CACHE_PREFIX}${userId}`, JSON.stringify(orders));
  } catch (error) {
    console.error('Error writing orders cache:', error);
  }
};

const mapOrderRecord = (order, itemsData = []) => ({
  id: order.id,
  date: order.created_at,
  status: order.status || 'Processing',
  total: order.total || 0,
  subtotal: order.subtotal || 0,
  shipping_cost: order.shipping_cost || 0,
  tax: order.tax || 0,
  itemCount: itemsData.length,
  estimatedDelivery: order.estimated_delivery,
  tracking_number: order.tracking_number,
  tracking_url: order.tracking_url,
  label_url: order.label_url,
  shippingAddress: order.shipping_address,
  payment_intent_id: order.payment_intent_id,
  items: itemsData.map((item) => ({
    id: item.product_id,
    name: item.product_name,
    price: item.price || 0,
    quantity: item.quantity || 1,
    selectedColor: item.selected_color,
    category: item.product_category,
    sku: item.product_sku,
    image: item.image,
  })),
});

const groupItemsByOrder = (orderItems = []) =>
  orderItems.reduce((grouped, item) => {
    if (!grouped[item.order_id]) grouped[item.order_id] = [];
    grouped[item.order_id].push(item);
    return grouped;
  }, {});

export const useOrders = () => {
  const context = useContext(OrdersContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrdersProvider');
  }
  return context;
};

export const OrdersProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user, loading: authLoading } = useAuth();
  const loadedUserIdRef = useRef(null);

  const syncOrdersCache = useCallback((userId, nextOrders) => {
    if (!userId) return;
    writeCachedOrders(userId, nextOrders);
    loadedUserIdRef.current = userId;
  }, []);

  const loadOrdersFromSupabase = useCallback(async (userId) => {
    const { data: ordersData, error: ordersError } = await supabase
      .from('orders')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (ordersError) {
      throw new Error(ordersError.message || 'Unable to load orders.');
    }
    if (!ordersData?.length) return [];

    const orderIds = ordersData.map((order) => order.id);
    const { data: orderItems, error: itemsError } = await supabase
      .from('order_items')
      .select('*')
      .in('order_id', orderIds);

    if (itemsError) {
      throw new Error(itemsError.message || 'Unable to load order items.');
    }

    const itemsByOrder = groupItemsByOrder(orderItems || []);
    return ordersData.map((order) => mapOrderRecord(order, itemsByOrder[order.id] || []));
  }, []);

  const fetchOrdersForUser = useCallback(async (userId, { force = false } = {}) => {
    if (!userId) return;
    if (!force && loadedUserIdRef.current === userId) return;

    const cached = readCachedOrders(userId);
    if (cached) {
      setOrders(cached);
      setError(null);
      setLoading(false);
    } else {
      setLoading(true);
      setError(null);
    }

    try {
      const ordersWithItems = await loadOrdersFromSupabase(userId);
      setOrders(ordersWithItems);
      syncOrdersCache(userId, ordersWithItems);
      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      if (!cached) {
        setError(err.message || 'Unable to load orders. Please try again.');
        setOrders([]);
      }
    } finally {
      setLoading(false);
    }
  }, [loadOrdersFromSupabase, syncOrdersCache]);

  useEffect(() => {
    if (authLoading) return;

    if (!user?.id) {
      loadedUserIdRef.current = null;
      setOrders([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const fetchOrders = async () => {
      if (loadedUserIdRef.current === user.id) return;

      const cached = readCachedOrders(user.id);
      if (cached) {
        setOrders(cached);
        setLoading(false);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const ordersWithItems = await loadOrdersFromSupabase(user.id);
        if (cancelled) return;
        setOrders(ordersWithItems);
        syncOrdersCache(user.id, ordersWithItems);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching orders:', err);
        if (!cached) {
          setError(err.message || 'Unable to load orders. Please try again.');
          setOrders([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchOrders();

    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading, loadOrdersFromSupabase, syncOrdersCache]);

  const refreshOrders = useCallback(() => {
    if (user?.id) {
      loadedUserIdRef.current = null;
      fetchOrdersForUser(user.id, { force: true });
    }
  }, [user?.id, fetchOrdersForUser]);

  const addOrder = useCallback((newOrder) => {
    setOrders((prevOrders) => {
      const next = [newOrder, ...prevOrders];
      if (user?.id) {
        writeCachedOrders(user.id, next);
      }
      return next;
    });
  }, [user?.id]);

  const updateOrder = useCallback((orderId, updates) => {
    setOrders((prevOrders) => {
      const next = prevOrders.map((order) =>
        order.id === orderId ? { ...order, ...updates } : order
      );
      if (user?.id) {
        writeCachedOrders(user.id, next);
      }
      return next;
    });
  }, [user?.id]);

  const value = {
    orders,
    loading,
    error,
    fetchOrdersForUser,
    refreshOrders,
    addOrder,
    updateOrder,
  };

  return (
    <OrdersContext.Provider value={value}>
      {children}
    </OrdersContext.Provider>
  );
};
